/**
 * @fileoverview Table rendering component with fixed and variable sections.
 * This component handles the rendering of tables with fixed left column and scrollable right sections.
 * @see {/src/components/ui/tables/Table.tsx}
 * @see {/src/hooks/useScrollInTable.ts}
 * @active
 */

import { useEffect, useState } from "react";

import { Flex } from "@chakra-ui/react";

import { EayTabPeople, RenderTabPeople } from "./Table";

/**
 * @interface TableRenderProps
 * @description Props for the TableRender component
 * @active
 */
interface TableRenderProps {
  /** Custom styles for the outer container */
  customStyleOuter?: Record<string, any>;
  /** Array of mask items for column configuration */
  mask: any[];
  /** Data array to be rendered in the table */
  data: any[];
  /** Styles for the fixed left column */
  tabFixedStyle?: Record<string, any>;
  /** Styles for the variable right section */
  tabVariableStyle?: Record<string, any>;
  /** Function for custom rendering of blocks within cells */
  blockSwitch?: ({
    item,
    mainItem,
    data,
  }: {
    item: any;
    mainItem: any;
    data: any[];
  }) => React.ReactNode;
  /** Modal component for EAY tab interactions */
  ModalComponent?: React.ComponentType<any>;
  /**
   * Whether this is a history table or history data
   * @deprecated This prop has inconsistent usage across components, accepting it as any for backward compatibility
   */
  history?: boolean | any[] | any;
}

/**
 * @function TableRender
 * @description Renders a complex table with fixed and scrollable sections
 * @param {TableRenderProps} props - Component props
 * @returns {JSX.Element} Rendered table component
 * @active
 */
export const TableRender = ({
  customStyleOuter = {},
  mask,
  data,
  tabFixedStyle = {},
  tabVariableStyle = {},
  blockSwitch,
  ModalComponent,
  history,
}: TableRenderProps): JSX.Element => {
  const [fixedTab, setFixedTab] = useState<any[] | null>(null);
  const [variableTab, setVariableTab] = useState<any[] | null>(null);
  const [isVisibleEay, setIsVisibleEay] = useState<boolean>(false);
  const [isShadow, setIsShadow] = useState<boolean>(false);

  const isPersonTable = data?.every((item) => item?.person?.nameFull);

  const newData = isPersonTable
    ? [...data]?.sort((a, b) =>
        a?.person?.nameFull > b?.person?.nameFull ? 1 : -1
      )
    : data;

  const findLastUniquePerson = isPersonTable
    ? newData.reduce((acc: Record<string, number>, item, index) => {
        if (item?.person?.nameFull) {
          acc[item.person.nameFull] = index;
        }
        return acc;
      }, {})
    : null;

  const lastPersonIndexArr = isPersonTable
    ? Object.values(findLastUniquePerson || {})
    : [];

  useEffect(() => {
    if (mask && mask?.length) {
      setFixedTab(mask.slice(0, 1));
      setVariableTab(mask.slice(1));
    }
  }, [mask]);

  return (
    <>
      {fixedTab && variableTab && (
        <Flex {...customStyleOuter} pos={"relative"}>
          <RenderTabPeople
            {...{
              mask: fixedTab,
              data: newData,
              tabContainerStyle: {
                w: { base: history ? "90px" : "160px", md: "fit-content" },
                ...tabFixedStyle,
              },
              setIsVisibleEay,
              blockSwitch,
              isShadow,
              lastPersonIndexArr,
            }}
          />
          <RenderTabPeople
            {...{
              mask: variableTab,
              data: newData,
              setIsVisibleEay,
              blockSwitch,
              tabContainerStyle: {
                flex: "1",
                overflow: "hidden",
                overflowX: "scroll",
                ...tabVariableStyle,
              },
              setIsShadow,
              lastPersonIndexArr,
            }}
          />
          {isVisibleEay && ModalComponent && (
            <EayTabPeople
              {...{
                data: newData,
                ModalComponent,
                lastPersonIndexArr,
              }}
            />
          )}
        </Flex>
      )}
    </>
  );
};
